<template>
  <VModal :showing="showing" container-classes="max-w-xl" @update:showing="cancel" @esc="cancel">
    <template #header>
      <h2 class="w-screen m-0 text-xl font-medium text-gray-900 leading-6 line-clamp-2">Create Secure Link</h2>
      <p class="mt-2 mb-0 text-sm text-gray-700">Share a secure link with people outside your company.</p>
    </template>

    <template #default>
      <VAlert :is-visible="!!state.error" banner-type="danger" class="mb-4">
        <template #content>{{ state.error }}</template>
      </VAlert>

      <form class="text-md space-y-4" data-testid="create-secure-link-form" @submit.prevent="">
        <FormField
          v-model="title"
          :attrs="titleAttrs"
          label="Label"
          required="required"
          placeholder="Label"
          autocomplete="new-username"
          :error="errors.title"
        />

        <div>
          <label class="text-sm font-normal">
            <div class="flex flex-row items-end justify-between mb-2">
              <label class="font-normal">Password</label>
              <label class="text-xs font-medium text-gray-500" type="optional">Optional</label>
            </div>

            <VInput
              v-model="password"
              v-bind="passwordAttrs"
              required
              class="mt-2"
              placeholder="Password"
              :error="!!errors.password"
              :maxlength="60"
            />
            <div v-if="!!errors.password" class="text-sm text-red-800">{{ errors.password }}</div>
          </label>

          <div class="mt-2 text-xs text-gray-700">
            Please choose a password that is a minimum of 8 characters and contains uppercase and lowercase letters, numbers and special
            characters.
          </div>
        </div>

        <div class="flex flex-col text-sm text-gray-900 form-field" data-testid="secure-link-email">
          <div class="flex flex-row items-end justify-between mb-2">
            <label class="font-medium">Recipient Email Addresses</label>
            <label class="text-xs font-medium text-gray-500" type="optional">Optional</label>
          </div>

          <VChipInput
            name="recipientEmails"
            :type="'email'"
            :value="model.email_addresses"
            placeholder="example@domain.com, example2@domain.com"
            @chip-input-error="state.chipInputError = $event"
            @chip-added="model.email_addresses = $event"
            @focus-prev-input="focusOnPassword"
            @focus-next-input="focusOnNotifyRecipients"
          />
        </div>

        <div class="flex items-start space-x-3">
          <VCheckbox id="notifyRecipients" v-model="model.notify_recipients" data-testid="secure-link-notify-checkbox" />
          <label class="cursor-pointer leading-4" for="notifyRecipients">
            <div class="text-sm">Notify Recipients</div>
            <div class="mt-1 text-xs text-gray-400">A secure link will be emailed to each recipient.</div>
          </label>
        </div>
      </form>
    </template>

    <template #footer>
      <div class="text-right space-x-4">
        <VButton data-testid="secure-link-cancel" @click="cancel">Cancel</VButton>
        <VButton variant="copper" data-testid="secure-link-save" @click="create">Create Secure Link</VButton>
      </div>
    </template>
  </VModal>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import { reactive, watch, toRef } from 'vue'
import { z } from 'zod'
import { useForm } from 'vee-validate'
import ZodMessages from '@/zod_presets/messages'
import { toTypedSchema } from '@vee-validate/zod'

import type { EditDeck } from '~/composables/useEditDeck'

const props = defineProps({
  deck: {
    type: Object as PropType<DeepReadonly<EditDeck>>,
    required: true,
  },
  showing: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['link-created', 'goto:secure-links', 'goto:secure-access', 'update:showing'])

const state = reactive({
  error: null,
  chipInputError: false,
})

const model = reactive({
  email_addresses: [],
  notify_recipients: false,
  deck_id: props.deck.id,
})

const Schema = z.object({
  title: z.string().nonempty(ZodMessages.title).max(60).default(''),
  password: z.string().optional().default(''),
})

const {
  handleSubmit,
  resetForm: reset,
  defineField,
  errors,
} = useForm<z.infer<typeof Schema>>({
  validationSchema: toTypedSchema(Schema),
})

const [title, titleAttrs] = defineField('title')
const [password, passwordAttrs] = defineField('password')

watch(
  toRef(props, 'showing'),
  () => {
    if (!props.showing) return
    state.error = null

    resetForm()
  },
  { immediate: true }
)

function cancel(): void {
  emit('goto:secure-links')
}

const create = handleSubmit(async () => {
  try {
    state.error = null

    if (state.chipInputError) {
      return
    }

    let payload: any = model

    if (password.value) {
      payload = { ...model, password: password.value }
    }

    payload = { ...payload, name: title.value }

    await $fetch(`/v2/decks/${props.deck.id}/uuids/`, {
      ...useFetchSharedOptions(),
      method: 'POST',
      body: JSON.stringify(payload),
    })

    emit('link-created')
    emit('goto:secure-links')
  } catch (e: any) {
    state.error = 'response' in e ? e.response.data.message : e.message
  }
})

function resetForm(): void {
  model.deck_id = props.deck.id
  model.email_addresses = []
  model.notify_recipients = false
  state.error = null

  reset()
}

function focusOnPassword(): void {
  const el = document.querySelector('#linkPassword input')
  if (el) {
    ;(el as HTMLInputElement).focus()
  }
}

function focusOnNotifyRecipients(): void {
  const el = document.querySelector('input#notifyRecipients')
  if (el) {
    ;(el as HTMLInputElement).focus()
  }
}
</script>
